// TEMPORARY: API interceptor disabled to fix ERR_INSUFFICIENT_RESOURCES
// The interceptor was causing infinite recursion with Clerk auth calls
// TODO: Re-enable with proper recursion guards for auth URLs
// import { apiInterceptor } from '@/lib/apiInterceptor';

import { createRoot } from 'react-dom/client';
import { logger } from '@/lib/logger';
import App from './App.tsx';
import './index.css';
import { MarkerSizeProvider } from './contexts/MarkerSizeContext';
import { featureFlags } from './lib/featureFlags';
import { monitoring } from '@/lib/monitoring';
import { initWebVitals } from '@/lib/webVitals';
import { ensureClerkSession, getCachedClerkUserId, getCachedOrganizationId } from '@/lib/auth';
// Analytics import removed to avoid circular dependency
// Will be initialized after monitoring

// Initialize monitoring and analytics
const initializeMonitoring = () => {
  try {
    // Initialize monitoring with Sentry
    monitoring.initialize({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE,
      release: import.meta.env.VITE_APP_VERSION,
      sampleRate: 1.0, // VF-43: capture ALL error events (was 0.1 → ~90% dropped in prod)
      tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1, // performance traces stay sampled (cost)
    });

    // Initialize Web Vitals monitoring
    initWebVitals();

    // TEMPORARY: API interceptor disabled (see line 1 comment)
    // logger.log('API interceptor initialized');

    // TEMPORARY: API interceptor test disabled
    // if (import.meta.env.DEV) {
    //   setTimeout(() => {
    //     logger.info('🧪 Testing API interceptor...');
    //     const testMetrics = apiInterceptor.getMetrics();
    //     logger.info('📊 Current metrics:', testMetrics);
    //   }, 1000);
    // }

    // Track app startup - deferred to avoid circular dependency
    logger.info('App started', {
      version: import.meta.env.VITE_APP_VERSION || 'unknown',
      environment: import.meta.env.MODE,
    });

    logger.log('Monitoring, analytics, and API interceptor initialized');
  } catch (error) {
    logger.error('Failed to initialize monitoring:', error);
  }
};

// Initialize feature flags
const initializeFeatureFlags = async () => {
  try {
    // Skip feature flags for public routes (no authentication needed)
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/shared/')) {
      logger.log('Skipping feature flags initialization for public route');
      return;
    }

    // Resolve Clerk context if available to keep storage in sync
    const clerkContext = await ensureClerkSession({ requireSignedIn: false });

    const cachedUserId = getCachedClerkUserId();
    const cachedOrgId = getCachedOrganizationId();
    const userId = clerkContext?.userId ?? cachedUserId ?? undefined;
    const orgId = clerkContext?.organizationId ?? cachedOrgId ?? undefined;

    let userGroups: string[] = [];
    try {
      const storedGroups = localStorage.getItem('user-groups');
      if (storedGroups) {
        userGroups = JSON.parse(storedGroups);
      }
    } catch (parseError) {
      logger.warn('Failed to parse cached user groups', parseError);
    }

    await featureFlags.initialize(userId, orgId, userGroups);

    if (userId) {
      // Deferred: analytics.identify would be called here
      logger.info('User identified', { userId, organizationId: orgId });
    }

    logger.log('Feature flags initialized');
  } catch (error) {
    logger.error('Failed to initialize feature flags:', error);
  }
};

// Initialize on app start
initializeMonitoring();
initializeFeatureFlags();

// Track user activity on interactions with throttling
let lastActivityTime = 0;
const ACTIVITY_THROTTLE_MS = 1000; // Max once per second

const trackActivity = () => {
  const now = Date.now();
  if (now - lastActivityTime < ACTIVITY_THROTTLE_MS) {
    return; // Skip if too soon since last track
  }
  lastActivityTime = now;

  const userId = getCachedClerkUserId();
  monitoring.trackUserActivity(userId || undefined);
};

document.addEventListener('click', trackActivity);
document.addEventListener('keydown', trackActivity);

// Handle app cleanup
window.addEventListener('beforeunload', () => {
  monitoring.cleanup();
  // Deferred: analytics.cleanup would be called here
  // TEMPORARY: API interceptor disabled (see line 1 comment)
  // apiInterceptor.cleanup();
});

createRoot(document.getElementById('root')!).render(
  <MarkerSizeProvider>
    <App />
  </MarkerSizeProvider>,
);
